"use client";

import { API_ENDPOINT } from "@/constants/appConstants";
import { useRegistrationProps } from "@/context/useRegistrationProps";
import { useCallback, useState } from "react";

import { useNetwork } from "./useNetwork";

/**
 * Fetches parsed resume / profile data already available on the backend.
 * Used after resume upload (once parsing completes) and on Continue manually
 * so registration can skip screens that already have data.
 */
const useParsedResumeData = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { hostname } = useRegistrationProps();
  const { fetchData } = useNetwork();

  const getProfileId = useCallback(async (): Promise<string> => {
    const response = await fetchData(
      `${API_ENDPOINT(hostname).middleware.profileDetails}?fields=personal_details`,
      "GET",
    );
    return (
      response?.data?.personalDetailSection?.personalDetails?.profileId || ""
    );
  }, [fetchData, hostname]);

  const fetchParsedResumeData = useCallback(
    async (profileId?: string, email?: string) => {
      setIsLoading(true);
      try {
        const id = profileId || (await getProfileId());
        if (!id) {
          return null;
        }

        let url = `${API_ENDPOINT(hostname).middleware.parsedResumeData}?profile_id=${id}`;
        if (email) {
          const decodedEmail = Buffer.from(
            decodeURIComponent(email),
            "base64",
          ).toString("utf-8");
          url += `&email=${decodedEmail}`;
        }
        const response = await fetchData(url, "GET");
        return response.data ?? null;
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("[useParsedResumeData] API error", error);
        return null;
      } finally {
        setIsLoading(false);
      }
    },
    [fetchData, getProfileId, hostname],
  );

  return { fetchParsedResumeData, getProfileId, isLoading };
};

export default useParsedResumeData;
